import React, { useMemo } from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import {
	useGetBrokerGroupDetailsQuery,
	useGetUserDetailsQuery,
	useUpdateBrokerMutation,
} from '@/features/user/userApi';
import { usePipelineContext } from '@/context/pipelineContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { FolioDrawerHeader } from '@/components/Drawers/FolioDrawer/FolioDrawerHeader';
import { BrokerageProfileDrawerForm } from './BrokerageProfileDrawerForm';

const parseBrokerAddress = (address) => {
	if (!address) return {};
	const [streetAddress, suburb, city, postcode] = String(address)
		.split(',')
		.map((part) => part.trim());
	return { streetAddress, suburb, city, postcode };
};

export const BrokerageProfileForm = ({ docId, drawerView = false }) => {
	const dispatch = useDispatch();
	const theme = useTheme();

	const account = useSelector((state) => state.msalAccount.account);
	const userEmail = account?.username;
	const [updateBroker, { isLoading: isSaving }] = useUpdateBrokerMutation();

	const { data: currentUser } = useGetUserDetailsQuery(
		{ email: userEmail },
		{ skip: !userEmail },
	);

	const {
		data: brokerGroup,
		isLoading: isLoadingProfile,
		isUninitialized: isProfileUninitialized,
		isError: isProfileError,
	} = useGetBrokerGroupDetailsQuery({ docId }, { skip: !docId });

	const { getFeatureFlags } = useFeatureFlagContext();
	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);
	const { active } = usePipelineContext();
	const canEdit = Boolean(currentUser?.principleUser);

	const defaultPrimary = theme.palette.primary.main;
	const initialValues = useMemo(() => {
		const addressParts = parseBrokerAddress(brokerGroup?.address);
		const conversionRates = {};
		if (showNewBusinessKPI) {
			(active ?? []).forEach((stage) => {
				if (
					typeof stage !== 'object' ||
					stage.pipelineId !== process.env.REACT_APP_PIPELINE_NEW_BUSINESS ||
					stage.isEndorsement ||
					!stage.id
				) {
					return;
				}
				conversionRates[stage.id] = brokerGroup?.conversionRates?.[stage.id] ?? 0;
			});
		}

		return {
			docId: brokerGroup?.id ?? docId ?? '',
			name: brokerGroup?.name ?? '',
			claimsEmail: brokerGroup?.claimsEmail ?? '',
			website: brokerGroup?.website ?? '',
			about: brokerGroup?.about ?? '',
			termsOfEngagement: brokerGroup?.termsOfEngagement ?? '',
			privacyStatement: brokerGroup?.privacyStatement ?? '',
			streetAddress: addressParts.streetAddress ?? '',
			suburb: addressParts.suburb ?? '',
			city: addressParts.city ?? '',
			postcode: addressParts.postcode ?? '',
			disclosureUrl: brokerGroup?.disclosureUrl ?? '',
			fullLogo: brokerGroup?.fullLogo ?? '',
			contactEmail: brokerGroup?.contactEmail ?? '',
			phoneNumber: (brokerGroup?.phoneNumber ?? '').toString(),
			fspNumber: (brokerGroup?.fspNumber ?? '').toString(),
			country: brokerGroup?.country ?? '',
			conversionRates,
			colours: {
				primary: brokerGroup?.colours?.primary || defaultPrimary,
				secondary: brokerGroup?.colours?.secondary || '#DADCFF',
				tertiary: brokerGroup?.colours?.tertiary || '#333333',
			},
		};
	}, [active, brokerGroup, defaultPrimary, docId, showNewBusinessKPI]);

	const isProfileLoading = Boolean(docId) && (isLoadingProfile || isProfileUninitialized);

	const handleSubmit = async (values) => {
		if (!canEdit || !values?.docId) return;

		const combinedAddress = [
			(values.streetAddress ?? '').trim(),
			(values.suburb ?? '').trim(),
			(values.city ?? '').trim(),
			(values.postcode ?? '').trim(),
		]
			.filter(Boolean)
			.join(', ');

		try {
			await updateBroker({
				docId: values.docId,
				properties: {
					name: values.name,
					claimsEmail: (values.claimsEmail ?? '').toLowerCase(),
					contactEmail: (values.contactEmail ?? '').toLowerCase(),
					phoneNumber: values.phoneNumber,
					website: values.website,
					about: values.about,
					fspNumber: values.fspNumber,
					termsOfEngagement: values.termsOfEngagement,
					privacyStatement: values.privacyStatement,
					address: combinedAddress,
					disclosureUrl: values.disclosureUrl,
					colours: values.colours,
					...(showNewBusinessKPI && { conversionRates: values.conversionRates }),
				},
			}).unwrap();
			dispatch(showToast({ message: 'Changes saved successfully.', success: true }));
		} catch (err) {
			console.error('Failed to save brokerage profile:', err);
			dispatch(
				showToast({
					message: 'There was an issue saving changes; please refresh and try again.',
					error: true,
				}),
			);
		}
	};

	return (
		<Stack sx={{ flex: 1, minHeight: 0, height: '100%', overflow: 'hidden' }}>
			{drawerView && <FolioDrawerHeader title='Brokerage profile' />}

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					px: 2,
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				{isProfileLoading ? (
					<Box display='flex' justifyContent='center' py={4}>
						<CircularProgress />
					</Box>
				) : isProfileError || !brokerGroup ? (
					<Typography sx={{ py: 2 }}>Unable to load this brokerage profile.</Typography>
				) : (
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							display: 'flex',
							flexDirection: 'column',
							overflow: 'hidden',
						}}
					>
						<Formik
							enableReinitialize
							initialValues={initialValues}
							onSubmit={handleSubmit}
						>
							<BrokerageProfileDrawerForm canEdit={canEdit} />
						</Formik>
					</Box>
				)}
			</Box>
		</Stack>
	);
};

export default BrokerageProfileForm;
